import { useParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { ProfileHeader } from "./profile-header"
import { SocialLinks } from "./social-links"
import { UserStats } from "./user-stats"
import { RecentActivity } from "./recent-activity"
import { FriendsList } from "./friends-list"
import { Container, Typography, Button } from '@mui/material';
import { Warning, Edit, Message, PersonAdd, PersonAddDisabled, Add, Check } from '@mui/icons-material';
import { translations } from '../../translations';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { LoginDialog } from '../LoginDialog';
import { useGoogleSignIn } from '../GoogleSignIn';

export default function UserDetails() {
  const { id } = useParams();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const language = localStorage.getItem('language') || 'ku';
  const { user: currentUser, login } = useAuth();
  const navigate = useNavigate();
  const [friendRequestSent, setFriendRequestSent] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const { promptGoogleSignIn } = useGoogleSignIn({
    onSuccess: () => {
      setShowLoginDialog(false);
    }
  });

  const isRtl = language === 'ku';
  const t = isRtl ? translations.ku : translations.en;

  const isOwnProfile = currentUser?.id === Number(id) || currentUser?.id === id;

  useEffect(() => {
    console.log('Current user:', currentUser);
    console.log('URL id parameter:', id);
    console.log('isOwnProfile:', isOwnProfile);
    const fetchUser: () => Promise<void> = async () => {
      try {
        const response = await fetch(`/api/users?id=${id}`);
        const data = await response.json();
        setUser(data.user);
      } catch (err) {
        setError('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleFriendRequest = async () => {
    if (!currentUser) {
      setShowLoginDialog(true);
      return;
    }

    try {
      const response = await fetch(`/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: currentUser.id,
          friendId: id
        })
      });
      if (response.ok) {
        setFriendRequestSent(true);
      }
    } catch (err) {
      console.error('Failed to send friend request:', err);
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      setShowLoginDialog(true);
      return;
    }

    try {
      const response = await fetch(`/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          followerId: currentUser.id,
          followedId: id
        })
      });
      if (response.ok) {
        setIsFollowing(true);
      }
    } catch (err) {
      console.error('Failed to follow user:', err);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div><Warning /> {error}</div>;
  if (!user) return <div>{t.userNotFound}</div>;

  return (
    <Container maxWidth="lg" dir={isRtl ? 'rtl' : 'ltr'}>
      <Typography 
        sx={{ 
          bgcolor: 'warning.light', 
          p: 2, 
          mb: 2, 
          borderRadius: 1,
          color: 'warning.dark'
        }}
      >
        {t.underConstruction}
      </Typography>
      
      <ProfileHeader 
        name={user.name}
        email={user.email}
        phone={user.phone}
        avatarUrl={user.profile_image_url}
        location={user.location}
        joinDate={user.join_date}
        isRtl={isRtl}
      />

      {isOwnProfile ? (
        <Button
          variant="contained"
          size="large"
          startIcon={<Edit />}
          sx={{ width: '100%', mb: 3, py: 1.5, fontSize: '1.1rem' }}
          onClick={() => navigate('/profile')}
        >
          {t.editProfile}
        </Button>
      ) : (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={friendRequestSent ? <Check /> : <PersonAdd />}
            disabled={friendRequestSent}
            onClick={handleFriendRequest}
            sx={{ flex: 1, py: 1.5 }}
          >
            {friendRequestSent ? t.friendRequestSent : t.addFriend}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={isFollowing ? <Check /> : <Add />}
            disabled={isFollowing}
            onClick={handleFollow}
            sx={{ flex: 1, py: 1.5 }}
          >
            {isFollowing ? t.following : t.follow}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<Message />}
            disabled
            sx={{ flex: 1, py: 1.5 }}
          >
            {t.message}
          </Button>
        </div>
      )}
      
      <SocialLinks links={user.social_links} isRtl={isRtl} />
      <UserStats 
        booksRead={user.books_read || 0}
        booksWishlist={user.books_wishlist || 0}
        avgRating={user.avg_rating || 0}
        followers={user.followers || 0}
        isRtl={isRtl}
      />
      <RecentActivity reviews={user.reviews} isRtl={isRtl} />
      <FriendsList friends={user.friends_list} isRtl={isRtl} />
      
      <LoginDialog 
        open={showLoginDialog} 
        onClose={() => setShowLoginDialog(false)} 
        onSuccess={() => {
          setShowLoginDialog(false);
        }}
        onSwitchToRegister={() => {
          setShowLoginDialog(false);
          navigate('/register');
        }}
        translations={t}
        handleGoogleButtonClick={() => {
          promptGoogleSignIn();
          setShowLoginDialog(false);
        }}
      />
    </Container>
  );
}